import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Text, Button, Input } from "react_reusable_components";
import { TwoColumn, TwoRow } from "../../core/layouts";
import TableActions from "../../components/TableActions";
import AddQuestionnaireQuestionContainer from "../AddQuestionnaireQuestionContainer";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import ThreeColumn from "../../core/layouts/ThreeColumn";
import { showNotification } from "../../UtilityFunctions/notification";
import { getColor, getLocalizedMessage } from "../../UtilityFunctions/utils";
import { QUESTION_TYPE_CONSTANTS } from "../../constants";
import Style from "./AddQuestionnaireSectionContainerStyle.module.scss";
import variables from "../../themes/base/styles/variables";

const AddQuestionnaireSectionContainerStyle = {
  mainDivContainer: {
    backgroundColor: variables.bodyBg,
    border: `1px solid ${variables.grey4}`,
    width: variables.treeDivWidthSmall,
    height: "fit-content",
    zIndex: 9,
    cursor: "pointer",
  },
  sectionDivBorderStyle: {
    width: "9px", //needed in px only for the border because of tree internal css
    height: variables.fullHeight,
    border: `1px solid ${variables.grey4}`,
  },
  rightSectionStyle: { flex: 1, alignContent: "flex-start" },
  sectionContentTopContainerStyle: {
    padding: variables.paddingSmall,
  },
  collapseIcon: {
    width: variables.iconWidth,
    height: variables.iconHeight,
    cursor: "pointer",
  },
  sectionHeading: {
    fontWeight: variables.fontWeightBold,
    height: "2rem",
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightSmall,
  },
  scrollableQuestionContainer: {
    display: variables.dFlex,
    flexDirection: "column",
    zIndex: 0,
    position: "relative",
    overflowY: "auto",
  },
};
const AddQuestionnaireSectionContainer = ({
  indexKey,
  sectionName,
  level,
  hasChildren,
  questionsData,
  setQuestionsData,
  handleExpand,
  hasQuestion,
  mode,
  evidenceInProgress,
  setEvidenceInProgress,
  isLastSection,
}) => {
  const intl = useIntl();
  const [openModal, setOpenModal] = useState(false);
  const containerRef = useRef(null);

  const addNewSection = () => {
    setQuestionsData((prevTreeData) => {
      const lastNode = prevTreeData[prevTreeData?.length - 1];
      const newKey = parseInt(lastNode?.sequence || 0) + 1;
      const newNode = {
        sectionName: "",
        sequence: String(newKey),
      };
      return [...prevTreeData, newNode];
    });
  };

  const addChildToParent = (e, childNodes) => {
    e.stopPropagation();
    handleExpand(indexKey, false, true);

    const addSectionToParent = (parent, newSequence, parentLevel) => {
      const sectionLevel = parentLevel ? parseInt(parentLevel) + 1 : 1;

      if (sectionLevel > 5) {
        showNotification("error", "Error", "manageQuestionnaire.maxLevelError");
        return;
      }

      const newNode = {
        sequence: newSequence,
        sectionName: "",
      };

      if (parent?.sections) {
        parent.sections.push(newNode);
      } else {
        parent.sections = [newNode];
      }
    };

    const traverseAndAddSection = (nodes, idxKey) => {
      for (const node of nodes) {
        if (node.sequence === idxKey) {
          let newSequence;
          if (node.sections && node.sections.length > 0) {
            const prevChildKey =
              node.sections[node.sections.length - 1].sequence;
            const parts = prevChildKey.split(".");
            const lastPart = parseInt(parts[parts.length - 1]);
            const incrementedPart = lastPart + 1;
            parts[parts.length - 1] = incrementedPart.toString();
            newSequence = parts.join(".");
          } else {
            newSequence = `${node.sequence}.1`;
          }

          addSectionToParent(node, newSequence, node.level);
          setQuestionsData([...questionsData]);
          return;
        }
        if (node.sections) {
          traverseAndAddSection(node.sections, idxKey);
        }
      }
    };

    traverseAndAddSection(childNodes, indexKey);
  };

  const renumberKeys = (nodes, parentKey) => {
    nodes.forEach((node, index) => {
      const newKey = parentKey ? `${parentKey}.${index + 1}` : `${index + 1}`;
      node.sequence = newKey;
      if (node.sections) {
        renumberKeys(node.sections, newKey);
      }
    });
  };

  const deleteNodeByKey = () => {
    const deleteNode = (nodes) => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].sequence === indexKey) {
          nodes.splice(i, 1);
          return true;
        }
        if (nodes[i].sections) {
          if (deleteNode(nodes[i].sections)) {
            return true;
          }
        }
      }
      return false;
    };

    const found = deleteNode(questionsData);
    if (found) {
      renumberKeys(questionsData);
      setQuestionsData([...questionsData]);
    } else {
      console.log("Node not found.");
    }
  };

  const handleTitleChange = (e) => {
    const newValue = e.target.value;
    const updateTitleByKey = (nodes) => {
      for (const node of nodes) {
        if (node.sequence === indexKey) {
          node.sectionName = newValue;
          return;
        }
        if (node.sections) {
          updateTitleByKey(node.sections);
        }
      }
    };

    updateTitleByKey(questionsData);
    setQuestionsData([...questionsData]);
  };

  function addQuestionToSection(sectionsData, sectionAddKey) {
    handleExpand(indexKey, false, true);

    const traverseAndAddQuestion = (sections, sectionKey) => {
      for (const section of sections) {
        if (section.sequence === sectionKey) {
          if (!section.questions) {
            section.questions = [];
          }

          const lastQuestion = section.questions[section.questions.length - 1];
          const sequence = lastQuestion ? lastQuestion.sequence + 1 : 1;

          const newQuestion = {
            sequence,
            name: "",
            questionType: [
              { type: QUESTION_TYPE_CONSTANTS.TEXTBOX, questionValue: [] },
            ],
            isMandatory: false,
          };

          section.questions.push(newQuestion);
          return;
        }

        if (section.sections) {
          traverseAndAddQuestion(section.sections, sectionKey);
        }
      }
    };

    traverseAndAddQuestion(sectionsData, sectionAddKey);
  }

  const handleDragOver = (event) => {
    const scrollThreshold = 50; // Define your scroll threshold in pixels
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const y = event.clientY;

    if (y <= rect.top + scrollThreshold) {
      container.scrollTop -= scrollThreshold;
    } else if (y >= rect.bottom - scrollThreshold) {
      container.scrollTop += scrollThreshold;
    }
  };
  const Element = mode === "view" ? Input.TextArea : Input;
  return (
    <div className={Style["title-wrapper"]}>
      {level === 1 && (
        <Text
          style={AddQuestionnaireSectionContainerStyle.sectionHeading}
          type="secondary"
        >
          Section {indexKey} of {questionsData?.length}
        </Text>
      )}
      <div
        id={indexKey}
        className={`tree-title ${!isLastSection && "new-title"}`} // keep this className to attach styles from library
        onClick={() => hasChildren && handleExpand(indexKey, true, true)}
        style={AddQuestionnaireSectionContainerStyle.mainDivContainer}
      >
        <TwoColumn
          leftSection={
            <div
              style={{
                ...AddQuestionnaireSectionContainerStyle.sectionDivBorderStyle,
                ...{ backgroundColor: getColor(level) },
              }}
            ></div>
          }
          rightSectionStyle={
            AddQuestionnaireSectionContainerStyle.rightSectionStyle
          }
          rightSection={
            <TwoRow
              topSectionStyle={{
                ...(mode !== "view" && {
                  alignItems: variables.alignCenter,
                }),
                ...AddQuestionnaireSectionContainerStyle.sectionContentTopContainerStyle,
              }}
              topSection={
                <>
                  {mode === "view" && (
                    <Text
                      className={Style["section-index-heading-style"]}
                      onClick={(e) => e.stopPropagation()}
                      style={{ lineHeight: variables.lineHeightXlarge }}
                    >
                      {indexKey}.
                    </Text>
                  )}
                  <Element
                    disabled={mode === "view"}
                    autoSize={{ minRows: 1, maxRows: 10 }}
                    prefix={
                      <Text
                        className={Style["section-index-heading-style"]}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {indexKey}.
                      </Text>
                    }
                    className={Style["name-input-style"]}
                    placeholder={getLocalizedMessage(
                      intl,
                      "manageQuestionnaire.namePlaceholderGeneral"
                    )}
                    onChange={handleTitleChange}
                    value={sectionName}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {!!hasChildren?.length && (
                    <img
                      style={AddQuestionnaireSectionContainerStyle.collapseIcon}
                      alt="collapseIcon"
                      src={"/icons/collapseIcon.png"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpand(indexKey, true, true);
                      }}
                    />
                  )}
                  {mode !== "view" && (
                    <TableActions
                      stopPropagation
                      tableActionsOptions={[
                        {
                          label: "Delete",
                          action: () => {
                            if (
                              questionsData?.[0]?.sequence === indexKey &&
                              questionsData?.length === 1
                            ) {
                              showNotification(
                                "error",
                                "Error",
                                getLocalizedMessage(
                                  intl,
                                  "manageQuestionnaire.minOneSectionRequired"
                                )
                              );
                            } else setOpenModal(true);
                          },
                        },
                      ]}
                    />
                  )}
                </>
              }
              bottomSection={
                mode !== "view" && (
                  <ThreeColumn
                    className={Style["button-wrapper"]}
                    leftSection={
                      level === 1 &&
                      parseInt(indexKey) === questionsData?.length && (
                        <Button
                          className={Style["add-new-section"]}
                          ghost
                          icon={null}
                          id="add-button"
                          onClick={addNewSection}
                        >
                          <img
                            alt={"icon"}
                            src="/icons/add.svg"
                            className={Style["icon"]}
                          />
                          {getLocalizedMessage(
                            intl,
                            "manageQuestionnaire.addNewSection"
                          )}
                        </Button>
                      )
                    }
                    middleSection={
                      level < 5 &&
                      !hasQuestion?.length && (
                        <Button
                          className={Style["add-new-section"]}
                          ghost
                          icon={null}
                          onClick={(e) => {
                            addChildToParent(e, questionsData);
                          }}
                        >
                          <img
                            alt={"icon"}
                            src="/icons/add.svg"
                            className={Style["icon"]}
                          />
                          {getLocalizedMessage(
                            intl,
                            "manageQuestionnaire.addNewSubSection"
                          )}
                        </Button>
                      )
                    }
                    rightSection={
                      !hasChildren?.length &&
                      !hasQuestion?.length && (
                        <Button
                          className={Style["add-new-section"]}
                          ghost
                          icon={null}
                          onClick={(e) => {
                            addQuestionToSection(questionsData, indexKey);
                          }}
                        >
                          <img
                            alt={"icon"}
                            src="/icons/add.svg"
                            className={Style["icon"]}
                          />
                          {getLocalizedMessage(
                            intl,
                            "manageQuestionnaire.addNewQuestion"
                          )}
                        </Button>
                      )
                    }
                  />
                )
              }
            />
          }
        />
      </div>
      <div
        style={
          AddQuestionnaireSectionContainerStyle.scrollableQuestionContainer
        }
        onDragOver={handleDragOver}
        ref={containerRef}
        id={`section-wrapper${indexKey}`}
      >
        {hasQuestion?.map?.((question, index) => {
          return (
            <AddQuestionnaireQuestionContainer
              key={question.id}
              question={question}
              index={index}
              hasQuestion={hasQuestion}
              questionsData={questionsData}
              indexKey={indexKey}
              addQuestionToSection={addQuestionToSection}
              handleExpand={handleExpand}
              setQuestionsData={setQuestionsData}
              mode={mode}
              evidenceInProgress={evidenceInProgress}
              setEvidenceInProgress={setEvidenceInProgress}
            />
          );
        })}
      </div>
      <ConfirmModalContainer
        open={openModal}
        setOpen={setOpenModal}
        question={getLocalizedMessage(
          intl,
          "manageQuestionnaire.deleteSection",
          { name: sectionName ? `"${sectionName}"` : "this" }
        )}
        iconSrc={"/icons/warningIcon.svg"}
        onConfirm={() => {
          deleteNodeByKey();
          setOpenModal(false);
        }}
      />
    </div>
  );
};

export default AddQuestionnaireSectionContainer;
